import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './AirportBoard.css';
import { getAirportBoard } from '../utils/httpApi';
import { getAccessToken } from '../utils/auth';
import { useSettings } from '../contexts/SettingsContext';
import { SUPPORTED_AIRPORTS } from '../utils/supportedAirports';
import { formatEtaClock } from '../utils/timezones';
import { isGroundVehicle } from '../utils/groundVehicle';
import { formatDurationMinutes } from './AircraftTableComponents';
import {
  FlagIcon, AirlineLogo, AircraftTypeIcon, GroundVehicleIcon, StatusBadge
} from './AircraftTableComponents';

/** Poll cadence while the page is visible. The board is ~15 fields per row, so this is cheap. */
export const BOARD_REFRESH_MS = 30_000;

const TABS = [
  { id: 'ground', label: 'On Ground', countKey: 'onGround' },
  { id: 'arrivals', label: 'Arrivals', countKey: 'arrivals' },
  { id: 'departures', label: 'Departures', countKey: 'departures' }
];

// Filter chips per tab. `test` receives the row; the first chip is "All".
const FILTERS = {
  ground: [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Moving', test: r => isMovingOnGround(r.groundState) },
    { id: 'parked', label: 'Parked', test: r => !isMovingOnGround(r.groundState) }
  ],
  arrivals: [
    { id: 'all', label: 'All' },
    { id: 'live', label: 'Live', test: r => r.status === 'LIVE' },
    { id: 'landed', label: 'Landed', test: r => r.status === 'LANDED' || r.status === 'LANDED_EST' }
  ],
  departures: [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active', test: r => isActiveDeparture(r.status) },
    { id: 'departed', label: 'Departed', test: r => r.status === 'DEPARTED' }
  ]
};

export function isMovingOnGround(groundState) {
  if (!groundState) return false;
  const s = groundState.toLowerCase();
  return s !== 'parked';
}

export function isActiveDeparture(status) {
  return status === 'POWER_ON' || status === 'TAXIING' || status === 'TAKE_OFF';
}

// Same palette PredictionsOnGround uses for its state badges.
const GROUND_STATE_LABELS = {
  taxiing_in: 'Taxiing In',
  taxiing_out: 'Taxiing Out',
  taxiing: 'Taxiing',
  parked: 'Parked',
  pushback: 'Pushback',
  holding: 'Holding',
  engines_on: 'Engines On'
};

// A landing the backend presumed from the airborne track (no surface fix):
// landingConfidence 60..99. NULL/100 is surface-confirmed.
function isPresumed(row) {
  return row.landingConfidence != null && row.landingConfidence < 100;
}
function presumedMark(row) {
  return isPresumed(row) ? '~' : '';
}

function groundStateLabel(state) {
  if (!state) return 'On ground';
  return GROUND_STATE_LABELS[state.toLowerCase()] || state;
}

/**
 * Status badge + the one detail line that matters for the row's tab. Kept as a
 * pure function of (tab, row) so the mapping is unit-testable.
 */
export function describeRow(tab, row, airportIcao) {
  const clock = t => (t ? formatEtaClock(t, airportIcao) : null);
  if (tab === 'ground') {
    const state = (row.groundState || '').toLowerCase();
    const cls = state === 'parked' || !state ? 'status-unknown'
      : state === 'taxiing_in' ? 'status-takeoff'
      : state === 'pushback' ? 'status-power-on'
      : 'status-taxiing';
    const parts = [];
    if (row.arrivedAt) parts.push(`Landed ${presumedMark(row)}${clock(row.arrivedAt)}`);
    if (row.timeOnGroundMinutes != null) parts.push(formatDurationMinutes(row.timeOnGroundMinutes));
    if (isPresumed(row)) parts.push(`${row.landingConfidence} %`);
    if (row.gate) parts.push(`Stand ${row.gate}`);
    return { label: groundStateLabel(row.groundState), cls, detail: parts.join(' · ') };
  }
  if (tab === 'arrivals') {
    const from = row.originIata ? `from ${row.originIata}` : null;
    switch (row.status) {
      case 'LIVE': {
        // No ETA when the row comes from the movement detector only (airports
        // without a landing model): distance and origin carry the line.
        const parts = [];
        if (row.etaTime) parts.push(`ETA ${clock(row.etaTime)}`);
        if (row.distanceKm != null) parts.push(`${Math.round(row.distanceKm)} km`);
        if (from) parts.push(from);
        return { label: 'Live', cls: 'status-live', dot: true, detail: parts.join(' · ') };
      }
      case 'LANDED':
        return { label: 'Landed', cls: 'status-landed', icon: isPresumed(row) ? '~' : '✓',
          detail: [`Landed ${presumedMark(row)}${clock(row.landedAt)}`, isPresumed(row) ? `${row.landingConfidence} %` : null,
            from, row.gate ? `Stand ${row.gate}` : null].filter(Boolean).join(' · ') };
      case 'LANDED_EST':
        return { label: 'Landed (est)', cls: 'status-landed', icon: '~',
          detail: [`ETA was ${clock(row.etaTime)}`, from].filter(Boolean).join(' · ') };
      default:
        return { label: 'No signal', cls: 'status-unknown',
          detail: [row.lastSeenAt ? `Last seen ${clock(row.lastSeenAt)}` : null, from].filter(Boolean).join(' · ') };
    }
  }
  // departures
  const to = row.destinationIata ? `to ${row.destinationIata}` : null;
  switch (row.status) {
    case 'DEPARTED':
      // liftoffEstimated: the time is the flight-phase detection (a minute or
      // two after the wheels left), not an observed liftoff — mark it like
      // LANDED_EST does.
      return { label: 'Departed', cls: 'status-landed', icon: row.liftoffEstimated ? '~' : '✓',
        detail: [`Airborne ${row.liftoffEstimated ? '~' : ''}${clock(row.liftoffAt)}`, to, row.runway ? `RWY ${row.runway}` : null].filter(Boolean).join(' · ') };
    case 'TAKE_OFF':
      return { label: 'Take-off', cls: 'status-takeoff', dot: true,
        detail: [row.runway ? `RWY ${row.runway}` : null, to].filter(Boolean).join(' · ') };
    case 'TAXIING':
      return { label: 'Taxiing', cls: 'status-taxiing', dot: true,
        detail: [row.ettTime ? `ETT ${clock(row.ettTime)}` : null, to].filter(Boolean).join(' · ') };
    case 'POWER_ON':
      return { label: 'Power on', cls: 'status-power-on', dot: true,
        detail: [row.gate ? `Stand ${row.gate}` : null, to].filter(Boolean).join(' · ') };
    default:
      return { label: 'Parked', cls: 'status-unknown',
        detail: [row.scheduledDeparture ? `STD ${clock(row.scheduledDeparture)}` : null, row.gate ? `Stand ${row.gate}` : null, to].filter(Boolean).join(' · ') };
  }
}

/**
 * Stable identity of a row within its list: the airframe plus the backend's
 * session identity (landing time / first prediction / taxi-out session start).
 * Callsign and ETA are NOT part of it — they change between polls and a
 * departure session can have no callsign at all, which made two such sessions
 * of one airframe collide on the same React key.
 */
export function rowKey(tab, row) {
  const id = tab === 'ground' ? row.arrivedAt
    : tab === 'arrivals' ? (row.sessionStartAt || row.etaTime)
    : (row.sessionStartAt || row.liftoffAt || row.ettTime);
  return `${row.icaoAddress || ''}|${id || ''}`;
}

/** Rows with a unique key, first occurrence wins. */
export function dedupeRows(tab, rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows) {
    const k = rowKey(tab, r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

/**
 * Mobile "Airport" page: the on-ground / arrivals / departures lists for one
 * airport, as current status per aircraft — the same flights the Predictions
 * page shows, without the prediction history. Polls
 * AirportViewService.getAirportBoard while visible.
 */
function AirportBoard({ receivers = [], selectedReceiverUuid, airportActivityUpdate = null, onJumpToHistory }) {
  const { colourSettings } = useSettings();
  const showGroundVehicles = colourSettings?.showGroundVehicles !== false;

  const selectedReceiver = useMemo(
    () => receivers.find(r => r.receiverUuid === selectedReceiverUuid) || null,
    [receivers, selectedReceiverUuid]
  );

  // Airport options: the prediction-supported airports, plus the selected
  // receiver's own airport if it is not one of them (e.g. a receiver at KSEA).
  const airportOptions = useMemo(() => {
    const opts = SUPPORTED_AIRPORTS.map(a => ({ icao: a.icao, name: a.name }));
    const own = selectedReceiver?.airportIcao?.toUpperCase();
    if (own && !opts.some(o => o.icao === own)) opts.unshift({ icao: own, name: own });
    return opts;
  }, [selectedReceiver]);

  const receiverAirport = selectedReceiver?.airportIcao?.toUpperCase() || null;
  const [airportIcao, setAirportIcao] = useState(
    () => receiverAirport || SUPPORTED_AIRPORTS[0].icao
  );
  // Follow the receiver's airport only when THAT changes (receiver switched or
  // its airport edited). Keyed on the string, not the receiver object: the
  // receivers array is rebuilt on every stats update, and depending on the
  // object snapped a user-chosen airport back to the receiver's within seconds.
  useEffect(() => {
    if (receiverAirport) setAirportIcao(receiverAirport);
  }, [receiverAirport]);

  const [tab, setTab] = useState('ground');
  const [filter, setFilter] = useState('all');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token || !airportIcao || inFlight.current) return;
    inFlight.current = true;
    try {
      const resp = await getAirportBoard(airportIcao, selectedReceiverUuid, token);
      if (resp?.header?.apiStatus === 'OK' || resp?.header?.apiStatus === 'SUCCESS') {
        setBoard(resp);
        setError(null);
      } else {
        setError('Airport data not available');
      }
    } catch (e) {
      setError('Airport data not available');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [airportIcao, selectedReceiverUuid]);

  // Initial load + poll while the tab is visible; refresh on return.
  useEffect(() => {
    setLoading(true);
    setBoard(null);
    load();
    let timer = null;
    const start = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        if (document.visibilityState === 'visible') load();
      }, BOARD_REFRESH_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') { load(); start(); }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  // A LANDING / DEPARTURE broadcast for this airport means a row has just
  // changed list: refresh now rather than at the next poll. load() ignores
  // the call while a request is already in flight.
  useEffect(() => {
    if (!airportActivityUpdate || !board) return;
    const at = airportActivityUpdate.airportIcao;
    if (at && at.toUpperCase() !== airportIcao) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportActivityUpdate]);

  // Reset the filter when switching tabs.
  const selectTab = useCallback((id) => { setTab(id); setFilter('all'); }, []);

  const rows = useMemo(() => {
    if (!board) return [];
    const list = tab === 'ground' ? board.onGround : tab === 'arrivals' ? board.arrivals : board.departures;
    let out = Array.isArray(list) ? list : [];
    if (!showGroundVehicles) out = out.filter(r => !isGroundVehicle(r));
    const chip = FILTERS[tab].find(f => f.id === filter);
    if (chip?.test) out = out.filter(chip.test);
    return dedupeRows(tab, out);
  }, [board, tab, filter, showGroundVehicles]);

  const counts = board?.counts || {};
  const airportName = board?.airportName || airportOptions.find(o => o.icao === airportIcao)?.name || airportIcao;

  return (
    <div className="airport-board">
      <div className="history-header-mobile airport-board-header">
        <select
          className="compact-receiver-select airport-board-select"
          value={airportIcao}
          onChange={(e) => setAirportIcao(e.target.value)}
          aria-label="Airport"
        >
          {airportOptions.map(o => (
            <option key={o.icao} value={o.icao}>{o.name}</option>
          ))}
        </select>
        <span className="airport-board-title" title={airportName}>{airportName}</span>
        <button
          type="button"
          className="filter-dropdown-btn airport-board-refresh"
          onClick={() => { setLoading(true); load(); }}
          aria-label="Refresh"
        >
          ↻
        </button>
      </div>

      <div className="history-content-tabs airport-board-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`content-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => selectTab(t.id)}
            onTouchEnd={(e) => { e.preventDefault(); selectTab(t.id); }}
          >
            {t.label}{counts[t.countKey] != null ? ` (${counts[t.countKey]})` : ''}
          </button>
        ))}
      </div>

      <div className="mobile-list-filters airport-board-filters">
        {FILTERS[tab].map(f => (
          <button
            key={f.id}
            className={`filter-btn${filter === f.id ? ' active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
        {board?.generatedAt && (
          <span className="airport-board-updated">
            {formatEtaClock(board.generatedAt, airportIcao)}
          </span>
        )}
      </div>

      <div className="airport-board-list">
        {loading && !board ? (
          <div className="loading-message">Loading airport data...</div>
        ) : error && !board ? (
          <div className="error-message">{error}</div>
        ) : rows.length === 0 ? (
          <div className="no-data">
            {tab === 'ground' ? 'No aircraft on the ground' : tab === 'arrivals' ? 'No arrivals today' : 'No departures today'}
          </div>
        ) : (
          rows.map(row => (
            <BoardRow
              key={rowKey(tab, row)}
              tab={tab}
              row={row}
              airportIcao={airportIcao}
              onSelect={onJumpToHistory}
            />
          ))
        )}
      </div>
    </div>
  );
}

function BoardRow({ tab, row, airportIcao, onSelect }) {
  const vehicle = isGroundVehicle(row);
  const d = describeRow(tab, row, airportIcao);
  const secondary = [row.registration, row.aircraftType].filter(Boolean).join(' · ');
  return (
    <button
      type="button"
      className="airport-board-row"
      onClick={() => onSelect && onSelect(row.icaoAddress, row.landedAt || row.arrivedAt)}
      title={row.icaoAddress}
    >
      <span className="airport-board-row-logo">
        {vehicle ? (
          <GroundVehicleIcon size={22} />
        ) : (
          <AirlineLogo airlineCode={row.airlineIcao} airlineName={row.airline} className="airline-logo" />
        )}
      </span>
      <span className="airport-board-row-main">
        <span className="airport-board-row-primary">
          <span className="airport-board-callsign">{row.callsign || row.icaoAddress}</span>
          {row.iataFlightNumber && row.iataFlightNumber !== row.callsign && (
            <span className="airport-board-flight">{row.iataFlightNumber}</span>
          )}
          {vehicle && <StatusBadge type="vehicle">VEHICLE</StatusBadge>}
          {row.military && <StatusBadge type="mil">MIL</StatusBadge>}
        </span>
        <span className="airport-board-row-secondary">
          <FlagIcon countryCode={row.countryCode} />
          {secondary && <span>{secondary}</span>}
          {row.aircraftType && <AircraftTypeIcon aircraftType={row.aircraftType} size={12} />}
        </span>
      </span>
      <span className="airport-board-row-status">
        <span className={d.cls}>
          {d.dot && <span className="status-dot" />}
          {d.icon && <span className="status-check">{d.icon}</span>}
          {d.label}
        </span>
        {d.detail && <span className="airport-board-row-detail">{d.detail}</span>}
      </span>
    </button>
  );
}

export default AirportBoard;
